<template>
    <spinner v-if="isLoading" />

    <Editor
        class="dm-tinymce-editor-textarea"
        :init="editorInit"
        tinymceScriptSrc="/tinymce/tinymce.min.js"
        @init="editorIsLoaded"
    />
</template>

<style>
.dm-tinymce-editor-textarea {
    visibility: hidden;
}
</style>

<script>
import Editor from "@tinymce/tinymce-vue";
import { purifyHtml } from "@/utilities/utilities.js";

export default {
    components: { Editor },

    props: ["toolbar", "modelValue", "purifyContent"], // toolbar is a string: "bold italic underline"

    data() {
        return {
            defaultToolbar:
                "undo redo | styles | h1 h2 h3 | bold italic underline | forecolor backcolor | " +
                "alignleft aligncenter alignright alignjustify | " +
                "bullist numlist outdent indent | removeformat | link | help",
            isLoading: true,
        };
    },

    emits: [
        "change",
        "blur",
        "input",
        "paste",
        "undo",
        "redo",
        "update:modelValue",
    ],

    computed: {
        editorInit() {
            return {
                base_url: "/tinymce", // Tell TinyMCE to load from public/tinymce
                suffix: ".min",

                menubar: false,

                plugins: [
                    // 'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview', 'anchor',
                    // 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                    // 'insertdatetime', 'media', 'table', 'paste', 'help', 'wordcount',
                    "link",
                    "autoresize",
                ],

                autoresize_bottom_margin: 20, // optional extra space at bottom
                autoresize_min_height: 400, // minimum editor height // Does not appear to take effect.
                autoresize_max_height: 800, // maximum editor height // Does not appear to take effect.

                link_default_target: "_blank",

                toolbar: this.toolbar || this.defaultToolbar,
                statusbar: false,

                style_formats: [
                    { title: "Heading 1", format: "h1" },
                    { title: "Heading 2", format: "h2" },
                    { title: "Heading 3", format: "h3" },
                    { title: "Heading 4", format: "h4" },
                    { title: "Paragraph", format: "p" },
                ],

                setup: (editor) => {
                    this.editor = editor;

                    // We handle v-model ourselves instead of letting tinymce-vue do it: tinymce-vue calls setContent()
                    // whenever the model differs from the editor, and purifyHtml() output always differs slightly
                    // (e.g. &rsquo; becomes ’), which reset the cursor to the start of the field on every keystroke.
                    editor.on("init", () => {
                        editor.setContent(this.modelValue || "");
                    });

                    // Registered before the "change" emit below, so parents saving on @change get the latest value.
                    editor.on("change input undo redo paste blur", () => {
                        this.updateValue(editor.getContent());
                    });

                    editor.on("change", () => {
                        this.$emit("change", editor.getContent());
                    });

                    editor.on("blur", () => {
                    this.$emit("blur", editor.getContent());
                    });

                    editor.on("input", () => {
                    this.$emit("input", editor.getContent());
                    });

                    editor.on("paste", () => {
                    this.$emit("paste", editor.getContent());
                    });

                    editor.on("undo", () => {
                        this.$emit("undo", editor.getContent());
                    });

                    editor.on("redo", () => {
                        this.$emit("redo", editor.getContent());
                    });
                },

                // Use self-hosted skins + content CSS
                skin_url: "/tinymce/skins/ui/oxide",
                content_css:
                    "/tinymce/skins/content/default/content.css?no_cache",
            };
        },
    },

    created() {
        // Kept non-reactive on purpose: the TinyMCE editor instance must not be wrapped in a Vue proxy.
        this.editor = null;
        this.lastEmittedValue = null;
    },

    watch: {
        // Only push values that come from outside (e.g. data loaded after mount), never our own emitted ones.
        modelValue(value) {
            if (!this.editor || !this.editor.initialized) return;
            if (value === this.lastEmittedValue) return;

            this.editor.setContent(value || "");
        },
    },

    methods: {
        updateValue(value) {
            value = this.purifyContent ? purifyHtml(value) : value;
            this.lastEmittedValue = value;
            this.$emit("update:modelValue", value);
        },

        editorIsLoaded() {
            this.isLoading = false;
        },
    },
};
</script>
